import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";

const COLUMN_ORDER = ["pending", "scale", "extend", "discard"];

const COLUMN_META = {
  pending: {
    label: "Pendiente",
    bg: "bg-slate-50 dark:bg-slate-900/30",
    border: "border-slate-200 dark:border-slate-700",
    header: "text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800",
  },
  scale: {
    label: "Escalar",
    bg: "bg-green-50 dark:bg-green-900/20",
    border: "border-green-200 dark:border-green-800",
    header: "text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/40",
  },
  extend: {
    label: "Extender",
    bg: "bg-amber-50 dark:bg-amber-900/20",
    border: "border-amber-200 dark:border-amber-800",
    header: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40",
  },
  discard: {
    label: "Descartar",
    bg: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-200 dark:border-red-800",
    header: "text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/40",
  },
};

export function PilotsKanban({ pilots, getInitiativeName, onDecisionChange }) {
  const getPilotsForColumn = (decision) =>
    pilots.filter((p) => (p.decision || "pending") === decision);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    await onDecisionChange(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMN_ORDER.map((colId) => {
          const meta = COLUMN_META[colId];
          const colPilots = getPilotsForColumn(colId);
          return (
            <div key={colId} className="flex flex-col min-w-0">
              <div
                className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-x border-t ${meta.border} ${meta.header}`}
              >
                <span className="font-semibold text-sm">{meta.label}</span>
                <span className="text-xs font-mono opacity-70">{colPilots.length}</span>
              </div>

              <Droppable droppableId={colId}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 min-h-[180px] p-2 space-y-2 rounded-b-lg border ${meta.border} ${meta.bg} transition-opacity ${
                      snapshot.isDraggingOver ? "opacity-80" : ""
                    }`}
                  >
                    {colPilots.map((pilot, index) => (
                      <Draggable key={pilot.id} draggableId={pilot.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Card
                              className={`transition-shadow select-none ${
                                snapshot.isDragging
                                  ? "shadow-xl rotate-1 opacity-95"
                                  : "cursor-grab active:cursor-grabbing hover:shadow-md"
                              }`}
                              data-testid={`kanban-pilot-${pilot.id}`}
                            >
                              <CardContent className="p-3 space-y-1.5">
                                <p className="font-medium text-sm leading-snug">{pilot.name}</p>
                                {pilot.objective && (
                                  <p className="text-xs text-muted-foreground line-clamp-2">
                                    {pilot.objective}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-1 pt-0.5">
                                  {pilot.initiative_id && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                      {getInitiativeName(pilot.initiative_id)}
                                    </Badge>
                                  )}
                                  {pilot.initiative_link_source === "manual_review" && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 dark:text-amber-400"
                                      title="Vínculo asignado manualmente tras revisión editorial"
                                      data-testid={`kanban-pilot-manual-link-badge-${pilot.id}`}
                                    >
                                      Vinculado manualmente
                                    </Badge>
                                  )}
                                  {pilot.cost_estimated && (
                                    <span className="text-[10px] text-muted-foreground font-mono">
                                      {new Intl.NumberFormat("es-ES", {
                                        style: "currency",
                                        currency: "EUR",
                                        maximumFractionDigits: 0,
                                      }).format(pilot.cost_estimated)}
                                    </span>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
